import React, { useState, useCallback, useEffect } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  Upload,
  Input,
  message,
  Select,
  Modal,
} from "antd";
import {
  CaretRightOutlined,
  PlusOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import DragUpload from "../Form/DragUpload";
import axios from "axios";
import config from "../../config/app";

import PageHeader from "../Layout/PageHeader";
import SupportTicketList from "./SupportTicketList";
import SupportTicketFilters from "./SupportTicketFilters";
import { ISSUE_CATEGORIES } from "../../constants/issue";
import { previewBase64Image } from "../../helpers/image";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";

const SupportTicket = () => {
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    export: false,
  });
  const [supportTickets, setSupportTickets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [submitters, setSubmitters] = useState([]);
  const [filters, setFilters] = useState({
    sort: "updatedAt",
    order: "descending",
    lastCommentedBy: undefined,
  });
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [form] = Form.useForm();

  const fetchSupportTickets = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, index: true }));

    try {
      const params = {
        limit: pagination.limit,
        page: pagination.currentPage,
        ...(filters.search && {
          searchField: filters.searchField,
          search: filters.search,
        }),
        startDate: filters.startDate?.startOf("day")?.toISOString(),
        endDate: filters.endDate?.endOf("day")?.toISOString(),
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
        ...(filters.assigneeId && { assigneeId: filters.assigneeId }),
        ...(filters.submittedBy?.length && { submittedBy: filters.submittedBy }),
        ...(filters.serviceProvider && {
          serviceProvider: filters.serviceProvider,
        }),
        ...(filters.lastCommentedBy && { lastCommentedBy: filters.lastCommentedBy }),
        sort: filters.sort,
        order: filters.order,
      };
      const res = await axios.get("/api/issues", { params });

      setSupportTickets(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }

    setLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [pagination.currentPage, pagination.limit, filters]);

  const fetchAssignees = useCallback(async () => {
    try {
      const res = await axios.get("/api/issues/assignees");
      setAssignees(res.data.data);
    } catch (error) {
      console.error("Error fetching assignees:", error);
    }
  }, []);

  const fetchSubmitters = useCallback(async () => {
    try {
      const res = await axios.get("/api/issues/submitters");
      setSubmitters(res.data.data);
    } catch (error) {
      console.error("Error fetching submitters:", error);
    }
  }, []);

  const exportDownload = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, export: true }));

    const params = {
      ...(filters.search && {
        searchField: filters.searchField,
        search: filters.search,
      }),
      startDate: filters.startDate?.startOf("day")?.toISOString(),
      endDate: filters.endDate?.endOf("day")?.toISOString(),
      ...(filters.status && { status: filters.status }),
      ...(filters.category && { category: filters.category }),
      ...(filters.assigneeId && { assigneeId: filters.assigneeId }),
      ...(filters.submittedBy?.length && { submittedBy: filters.submittedBy }),
      ...(filters.serviceProvider && {
        serviceProvider: filters.serviceProvider,
      }),
    };

    const messageContent = (
      <p>
        Are you sure you want to download {pagination.totalSize} tickets data ?
        <br />
      </p>
    );

    Modal.confirm({
      content: messageContent,
      title: `Export Ticket List ? `,
      async onOk() {
        await axios
          .post("/api/issues/export", {}, { params })
          .then((res) => {
            message.info(`Please check your email after 5-20 minute`);
          })
          .catch((error) => {
            message.error(error.message);
          });
      },
    });

    setLoading((prevLoading) => ({ ...prevLoading, export: false }));
  }, [filters, pagination.totalSize]);

  useEffect(() => {
    fetchSupportTickets();
  }, [pagination.currentPage, pagination.limit, fetchSupportTickets]);

  useEffect(() => {
    fetchAssignees();
    fetchSubmitters();
  }, [fetchAssignees, fetchSubmitters]);

  const createHandler = async (newIssue) => {
    setLoading((prevLoading) => ({ ...prevLoading, create: true }));

    try {
      await axios.post(
        "/api/issues",
        {
          ...newIssue,
          attachments: uploadedFiles,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          formSerializer: {
            indexes: null,
          },
        }
      );

      form.resetFields();
      message.success("Issue created");
      fetchSupportTickets();
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }

    setLoading((prevLoading) => ({ ...prevLoading, create: false }));
  };

  return (
    <div>
      <PageHeader
        title="Support Tickets"
        childRoutes={[
          { path: "/support-tickets", breadcrumbName: "Support Tickets" },
        ]}
      />

      <Collapse
        defaultActiveKey={"filters"}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <SupportTicketFilters
            filters={filters}
            assignees={assignees}
            submitters={submitters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      {(permissions?.includes(PERMISSIONS.CREATE_ISSUES) || permissions?.includes(PERMISSIONS.COMMENT_ISSUES)) && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          {permissions?.includes(PERMISSIONS.CREATE_ISSUES) && (
            <Collapse.Panel header="Create New Issue" key="create-issue-section">
              <Form
                form={form}
                name="new-issue-form"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                autoComplete="off"
                onFinish={createHandler}
                initialValues={{
                  category: [ISSUE_CATEGORIES.DELIVERED_NOT_RECEIVED.key],
                  type: "ADMIN_ORDER",
                }}
              >
                <Form.Item label="Type" name="type">
                  <Select style={{ width: 200 }}>
                    <Select.Option value="ADMIN_ORDER">ORDER</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.category !== curr.category}>
                  {({ getFieldValue }) => {
                    const selectedCategory = getFieldValue("category") || [];

                    return (
                      <Form.Item label="Category" name="category">
                        <Select style={{ width: 200 }} mode="multiple">
                          {Object.values(ISSUE_CATEGORIES).map((category) => (
                            <Select.Option
                              key={category.key}
                              value={category.key}
                              disabled={
                                (category.key === ISSUE_CATEGORIES.CLAIM.key &&
                                  selectedCategory.includes(
                                    ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key
                                  )) ||
                                (category.key ===
                                  ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key &&
                                  selectedCategory.includes(ISSUE_CATEGORIES.CLAIM.key))
                              }
                            >
                              {category.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                <Form.Item
                  label="Tracking No"
                  name="trackingNo"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label="Description"
                  name="remark"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item label="Attachments" name="attachments">
                  <Upload
                    beforeUpload={() => false}
                    listType="picture-card"
                    onChange={(e) => {
                      if (e.file.status === "removed") {
                        setUploadedFiles((prev) =>
                          prev.filter((file) => file.uid !== e.file.uid)
                        );
                        return;
                      }
                      setUploadedFiles((prev) => [...prev, e.file]);
                    }}
                    onPreview={(file) => previewBase64Image(file)}
                    maxCount={10}
                    multiple
                  >
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  </Upload>
                </Form.Item>

                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                  <Button loading={loading.create} type="primary" htmlType="submit">
                    Create
                  </Button>
                </Form.Item>
              </Form>
            </Collapse.Panel>
          )}

          {permissions?.includes(PERMISSIONS.COMMENT_ISSUES) && (
            <Collapse.Panel
              header="Bulk Update Comments"
              key="bulk-update-comments"
            >
              <DragUpload
                action={`${process.env.REACT_APP_SERVER_URL}/api/issues/bulk-comments`}
                accept=".xls, .xlsx, .csv"
                method="post"
                onUploadSuccess={(_) => fetchSupportTickets()}
              />
              <br />
              <Button
                onClick={() => {
                  window.open(
                    `${config.fileTemplatePath}/Support-Ticket-Bulk-Comment-Template.xlsx`
                  );
                }}
                className="mt-2"
              >
                <DownloadOutlined /> Template
              </Button>
            </Collapse.Panel>
          )}
        </Collapse>
      )}

      <Row className="mt-2 mb-2" justify="space-between" align="middle">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />

          <span color="blue">
            <Button onClick={exportDownload} loading={loading.export}>
              Export <DownloadOutlined></DownloadOutlined>
            </Button>
          </span>
        </Col>
      </Row>

      <SupportTicketList
        supportTickets={supportTickets}
        loading={loading.index}
        assignees={assignees}
      />

      <Pagination
        total={pagination?.totalSize}
        pageSize={pagination?.limit}
        onChange={(currentPage, limit) => {
          setPagination({ ...pagination, limit, currentPage });
        }}
        current={pagination.currentPage}
      />
    </div>
  );
};

export default SupportTicket;
